import { cva, VariantProps } from 'class-variance-authority';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/icons';

/**
 * The tile box itself — shared with DateTile so the two tile flavours can't
 * drift apart. Pass `size: null` to opt out of the icon-sized padding when the
 * tile holds something other than an icon.
 */
export const tileSurface = cva('inline-flex rounded-s', {
  variants: {
    intent: {
      accent: 'bg-accent-subtle',
      default: 'bg-surface-strong',
      positive: 'bg-positive-subtle',
      warning: 'bg-warning-subtle',
      negative: 'bg-negative-subtle',
      info: 'bg-info-subtle',
      disabled: 'bg-surface-disabled',
    },
    size: {
      large: 'p-[10px]',
      xl: 'p-[12px]',
    },
  },
  defaultVariants: {
    intent: 'default',
    size: 'large',
  },
});

/**
 * Content colour matching each `tileSurface` intent. The variant key stays
 * `iconColor` because it drives `IconTile`'s public `iconColor` prop.
 */
export const tileContentColor = cva('', {
  variants: {
    iconColor: {
      default: 'text-content-default',
      accent: 'text-accent',
      positive: 'text-content-positive',
      warning: 'text-content-warning',
      negative: 'text-content-negative',
      info: 'text-content-info',
      disabled: 'text-content-subdued',
    },
  },
  defaultVariants: {
    iconColor: 'default',
  },
});

export const IconTile = ({
  icon,
  intent,
  iconColor: iconColorOverride,
  size = 'large',
}: { icon: IconName } & VariantProps<typeof tileSurface> &
  VariantProps<typeof tileContentColor>) => {
  return (
    <div className={tileSurface({ intent, size })}>
      <Icon
        name={icon}
        size={size}
        color={tileContentColor({ iconColor: iconColorOverride ?? intent })}
      />
    </div>
  );
};
